import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { ThemeProvider } from "@/components/theme-provider";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/Dashboard";
import MenuManagement from "./pages/MenuManagement";
import TablesManagement from "./pages/TablesManagement";
import OrdersManagement from "./pages/OrdersManagement";
import OrderHistory from "./pages/OrderHistory";
import StoreManagement from "./pages/StoreManagement";
import StaffManagement from "./pages/StaffManagement";
import StaffAttendance from "./pages/StaffAttendance";
import SettingsPage from "./pages/SettingsPage";
import PublicMenu from "./pages/PublicMenu";
import BlogPage from "./pages/BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import ActivityLog from "./pages/ActivityLog";
import MenuSandboxPreview from "./pages/MenuSandboxPreview";
import SuperAdminView from "./pages/SuperAdminView";
import ChatPage from "./pages/ChatPage";
import { isSuperAdmin } from "./utils/roleUtils";
import { useAuthStore } from "./store/authStore";
import AcceptInvitation from "./pages/AcceptInvitation";
import { useTranslation } from "react-i18next";
import InventoryManagement from "./pages/InventoryManagement";
import FaqPage from "./pages/FaqPage";
import PublicReservationBooking from "./pages/PublicReservationBooking";
import ReservationsManagement from "./pages/ReservationsManagement";
import { DashboardOverviewComponent } from "@/components/dashboard/DashboardOverview";
import StoreFront from "./pages/StoreFront";
import { BeautyServices } from "./pages/BeautyServices";
import LandingPageBuilder from "./pages/LandingPageBuilder";
import NotificationsPage from "./pages/NotificationsPage";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { fcmService } from "./services/fcmService";

import { supabase } from "./integrations/supabase/client";
import PaymentCallback from "@/pages/payment/callback";
import PublicStorefrontPage from "./pages/PublicStorefrontPage";
import { isNativePlatform, isWebPlatform } from "@/utils/platformDetection";
import { useFCMNavigation } from "@/hooks/useFCMNavigation";

// Analytics tracking component
const RouteChangeTracker = () => {
  const { pathname } = useLocation();

  // This will be used by the useAnalytics hook
  return null;
};

const queryClient = new QueryClient();

// Public Routes
const PublicRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/menu" element={<PublicMenu />} />
      <Route path="/store/:slug" element={<PublicStorefrontPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/book/:storeId" element={<PublicReservationBooking />} />
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// Add new IndustryProtectedRoute component
const IndustryProtectedRoute = ({ children, isFnBIndustry }) => {
  const location = useLocation();

  // Define FnB-specific routes
  const fnbRoutes = [
    "menu",
    "tables",
    "qr",
    "inventory",
    "dashboard",
    "orders",
    "landing-page",
  ];

  // Define Beauty Services specific routes
  const beautyRoutes = ["services"];

  // Get the current route path
  const currentPath = location.pathname.split("/")[2] || ""; // Get the route after /dashboard/

  // If it's FnB industry, only allow FnB routes
  if (isFnBIndustry && !fnbRoutes.includes(currentPath)) {
    return <Navigate to="/dashboard" replace />;
  }

  // If it's not FnB industry, only allow beauty service routes
  if (!isFnBIndustry && !beautyRoutes.includes(currentPath)) {
    return <Navigate to="/dashboard/services" replace />;
  }

  return children;
};

// Protected Dashboard Routes
const DashboardRoutes = () => {
  const { selectedStoreId, isFnBIndustry } = useStoreManagementStore();

  return (
    <Routes>
      <Route path="/" element={<Dashboard />}>
        <Route
          index
          element={
            <IndustryProtectedRoute isFnBIndustry={isFnBIndustry}>
              <DashboardOverviewComponent storeId={selectedStoreId || ""} />
            </IndustryProtectedRoute>
          }
        />
        <Route
          path="menu"
          element={
            <IndustryProtectedRoute isFnBIndustry={isFnBIndustry}>
              <MenuManagement />
            </IndustryProtectedRoute>
          }
        />
        <Route
          path="tables"
          element={
            <IndustryProtectedRoute isFnBIndustry={isFnBIndustry}>
              <TablesManagement />
            </IndustryProtectedRoute>
          }
        />
        <Route
          path="orders"
          element={
            <IndustryProtectedRoute isFnBIndustry={isFnBIndustry}>
              <OrdersManagement />
            </IndustryProtectedRoute>
          }
        />
        <Route path="history" element={<OrderHistory />} />
        <Route path="activity" element={<ActivityLog />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route
          path="inventory"
          element={
            <IndustryProtectedRoute isFnBIndustry={isFnBIndustry}>
              <InventoryManagement />
            </IndustryProtectedRoute>
          }
        />
        <Route path="menu-sandbox" element={<MenuSandboxPreview />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="staff" element={<StaffManagement />} />
        <Route path="attendance" element={<StaffAttendance />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="store" element={<StoreManagement />} />
        <Route path="blog" element={<BlogPage />} />
        <Route path="blog/:id" element={<BlogDetailPage />} />
        <Route
          path="reservations"
          element={
            // <IndustryProtectedRoute isFnBIndustry={isFnBIndustry}>
            <ReservationsManagement />
            // </IndustryProtectedRoute>
          }
        />
        <Route
          path="landing-page"
          element={
            <IndustryProtectedRoute isFnBIndustry={isFnBIndustry}>
              <LandingPageBuilder />
            </IndustryProtectedRoute>
          }
        />
        <Route
          path="services"
          element={
            <IndustryProtectedRoute isFnBIndustry={isFnBIndustry}>
              <BeautyServices />
            </IndustryProtectedRoute>
          }
        />
        <Route path="stores" element={<StoreManagement />} />
        <Route
          path="super-admin"
          element={
            <ProtectedRoute checkRole={(user) => isSuperAdmin(user?.role)}>
              <SuperAdminView />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="storefront" element={<StoreFront />} />
    </Routes>
  );
};

function App() {
  const { user } = useAuthStore();
  const { i18n } = useTranslation();

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event) => {
        try {
          if (user && isNativePlatform()) {
            fcmService.setNavigate(() => {
              console.warn('Navigation function not available');
            });
          }
        } catch (error) {
          console.error("Error cleaning up FCM on logout:", error);
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    // Set initial theme based on user preference or system preference
    const storedTheme = localStorage.getItem("theme") || "system";
    document.documentElement.dataset.theme = storedTheme;

    const initializeServices = async () => {
        try {
        
          // Register for push notifications
          await fcmService.initialize();
        } catch (error) {
          console.error("Error initializing native FCM:", error);
        }
      // }

      // Initialize web FCM for web platform only
      if (isWebPlatform()) {
        try {
          // await registerWebFcmToken();
        } catch (error) {
          console.error("Error initializing web push notifications:", error);
        }
      }

      // Note: Customer notification service has been replaced with the new orderNotification.service.ts
    };

    // Initialize services if user is logged in
    if (user && isNativePlatform()) {
      initializeServices();
    }

    // Cleanup function
    return () => {
      // Clean up FCM service
      fcmService.setNavigate(() => {
        console.warn('Navigation function not available');
      });
    };
  }, [user]); // Re-run when user changes

  // Initialize language if no user is logged in (for public pages)
  useEffect(() => {
    const browserLang = navigator.language.substring(0, 2);
    // Only use browser language if it's supported, otherwise fall back to English
    const languageToUse = ["en", "de", "es"].includes(browserLang)
      ? browserLang
      : "en";
    i18n.changeLanguage(languageToUse);

    // Note: Customer notification service has been replaced with the new orderNotification.service.ts
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="system" storageKey="theme">
          <Router>
            <AppContent />
            <RouteChangeTracker />
            <Routes>
              <Route path="/*" element={<PublicRoutes />} />
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute>
                    <DashboardRoutes />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <Toaster />
          </Router>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

// Separate component to use the hook inside Router context
function AppContent() {
  useFCMNavigation();
  
  return null; // This component doesn't render anything
}

export default App;
